import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import type { Question } from '@/types/form'

interface TotalSettingsProps {
  question: Question
  questions: Question[]
  onChange: (patch: Partial<Question['settings']>) => void
}

function numericValue(value: string) {
  if (value.trim() === '') return undefined
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : undefined
}

export function TotalSettings({ question, questions, onChange }: TotalSettingsProps) {
  const sourceIds = (question.settings.totalSourceQuestionIds ?? []).join(', ')
  const numericQuestions = questions.filter(
    (item) =>
      item.id !== question.id &&
      ['NUMBER', 'NUMBER_SLIDER', 'TOTAL'].includes(item.type) &&
      item.type !== 'DIVIDER',
  )
  const selectedSourceIds = new Set(question.settings.totalSourceQuestionIds ?? [])
  const previewValue = Number(question.settings.totalPreviewValue ?? 0)

  return (
    <div className="flex flex-col gap-4 rounded-lg border border-slate-200 p-3">
      <div>
        <h4 className="text-sm font-semibold text-slate-950">加總計算設定</h4>
        <p className="mt-1 text-xs leading-5 text-slate-500">
          前端先建立計算欄位 contract；正式後端接線後可用題目 ID 或欄位代碼計算。
        </p>
      </div>

      <div className="flex flex-col gap-2">
        <Label>計算方式</Label>
        <Select
          value={question.settings.totalMode ?? 'sum'}
          onValueChange={(totalMode) =>
            onChange({ totalMode: totalMode as Question['settings']['totalMode'] })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="sum">加總</SelectItem>
            <SelectItem value="average">平均</SelectItem>
            <SelectItem value="count">計數</SelectItem>
            <SelectItem value="custom">自訂公式</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-col gap-2">
        <Label>來源題目選擇</Label>
        <div className="grid gap-2">
          {numericQuestions.length > 0 ? (
            numericQuestions.map((sourceQuestion) => {
              const selected = selectedSourceIds.has(sourceQuestion.id)
              return (
                <button
                  key={sourceQuestion.id}
                  type="button"
                  onClick={() =>
                    onChange({
                      totalSourceQuestionIds: selected
                        ? [...selectedSourceIds].filter((id) => id !== sourceQuestion.id)
                        : [...selectedSourceIds, sourceQuestion.id],
                    })
                  }
                  className={`rounded-md border px-3 py-2 text-left text-sm transition ${
                    selected
                      ? 'border-indigo-200 bg-indigo-50 text-indigo-700'
                      : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  {sourceQuestion.title}
                </button>
              )
            })
          ) : (
            <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 p-3 text-sm text-slate-500">
              尚未有可作為來源的數字題。可先新增數字欄位或數字滑桿。
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <Label>來源題目 ID / 欄位代碼</Label>
        <Input
          value={sourceIds}
          placeholder="例如：q_budget, q_quantity"
          onChange={(event) =>
            onChange({
              totalSourceQuestionIds: event.target.value
                .split(',')
                .map((item) => item.trim())
                .filter(Boolean),
            })
          }
        />
        <p className="text-xs leading-5 text-slate-500">
          這裡可輸入內部欄位代碼；填答頁只會顯示來源數與公式摘要，不會顯示原始題目 ID。
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-2">
          <Label>預覽數值</Label>
          <Input
            type="number"
            value={String(question.settings.totalPreviewValue ?? '')}
            onChange={(event) => onChange({ totalPreviewValue: numericValue(event.target.value) })}
          />
        </div>
        <div className="flex flex-col gap-2">
          <Label>單位</Label>
          <Input
            value={question.settings.totalUnit ?? ''}
            placeholder="例如：元"
            onChange={(event) => onChange({ totalUnit: event.target.value })}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-2">
          <Label>顯示前綴</Label>
          <Input
            value={question.settings.totalPrefix ?? ''}
            placeholder="例如：預估合計"
            onChange={(event) => onChange({ totalPrefix: event.target.value })}
          />
        </div>
        <div className="flex flex-col gap-2">
          <Label>顯示後綴</Label>
          <Input
            value={question.settings.totalSuffix ?? ''}
            placeholder="例如：未稅"
            onChange={(event) => onChange({ totalSuffix: event.target.value })}
          />
        </div>
      </div>

      {question.settings.totalMode === 'custom' ? (
        <div className="flex flex-col gap-2">
          <Label>自訂公式 preview</Label>
          <Textarea
            value={question.settings.totalFormula ?? ''}
            placeholder="例如：(q_budget + q_service_fee) * 1.05"
            className="min-h-24 font-mono text-xs"
            onChange={(event) => onChange({ totalFormula: event.currentTarget.value })}
          />
        </div>
      ) : null}

      <div className="rounded-md border border-indigo-100 bg-indigo-50 p-3">
        <div className="text-xs font-medium text-indigo-700">mock-safe 計算結果</div>
        <div className="mt-1 text-2xl font-semibold text-indigo-950">
          {question.settings.totalPrefix ?? '計算結果'}{' '}
          {new Intl.NumberFormat('zh-TW').format(previewValue)}
          {question.settings.totalUnit ? ` ${question.settings.totalUnit}` : ''}
          {question.settings.totalSuffix ? ` ${question.settings.totalSuffix}` : ''}
        </div>
        <div className="mt-2 text-xs text-indigo-700">
          {describeFormula(question, selectedSourceIds.size)}
        </div>
      </div>
    </div>
  )
}

function describeFormula(question: Question, sourceCount: number) {
  if (question.settings.totalMode === 'average') return `平均 ${sourceCount} 個來源題目的數值。`
  if (question.settings.totalMode === 'count') return `計算 ${sourceCount} 個來源題目的填答數。`
  if (question.settings.totalMode === 'custom') {
    return question.settings.totalFormula ? '自訂公式已設定。' : '尚未輸入自訂公式。'
  }
  return `加總 ${sourceCount} 個來源題目的數值。`
}
